import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import CorrectAnswerInput from '../ui/CorrectAnswer';
import Timer from '../ui/Timer';
import { useAuth } from '../../context/AuthContext';
import { setUserProgress } from '../../config/api';
import { SetUserProgressRequest } from '../../types/progress';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Flame } from 'lucide-react';
import ProgressBar from '../shared/ProgressBar';
import { Fact } from '../../types';
import FluencyTextInput from './FluencyTextInput';
import { logError } from '../../utils/errorReporting';
import { progressQueue } from '../../services/ProgressQueueService';
import { DEFAULT_TRACK } from '../../types/constants';
import {
  getAccommodationMasteryThreshold,
  getAccommodationTimerDuration,
  isAccommodationTimerUntimed
} from '../../policies/studentAccommodationPolicy';

// Fact mastery utilities
const OTHER_MASTERY_THRESHOLD = 1;
const MAX_ATTEMPTS_PER_FACT = 5;
// Define timer duration constants for fluency practice
// const TIMER_GREEN_ZONE_DURATION = 4; // seconds for green zone

interface QuestionScreenProps {
  questions: Fact[];
  practiceFacts: string[]; // Array of PK values for practice facts
  otherFacts: string[]; // Array of PK values for other facts
  onComplete: (masteredPracticeFactCount?: number) => void;
  timerDuration?: number; // Duration of green zone in seconds
  onAnswerResult?: (correct: boolean) => void; // Callback after each answer
  onTimerWarning?: () => void; // Callback when timer enters yellow zone
  hideKeypad?: boolean; // Hide the touchpad number keypad
  immediateProgress?: boolean; // Skip backend batching for time-sensitive progress updates
  currentStreak?: number; // Live streak count from parent
  sessionBestStreak?: number; // Session best streak from parent
}

const QuestionScreen: React.FC<QuestionScreenProps> = ({
  questions: facts,
  practiceFacts,
  otherFacts,
  onComplete,
  timerDuration = 6, // Default to 6 seconds if not provided
  onAnswerResult,
  onTimerWarning,
  hideKeypad,
  immediateProgress,
  currentStreak,
  sessionBestStreak,
}) => {
  const navigate = useNavigate();
  const trackId = sessionStorage.getItem('activeTrackId') || DEFAULT_TRACK;
  
  // State hooks
  const [currentIndex, setCurrentIndex] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isProcessingEnd, setIsProcessingEnd] = useState(false);
  const [showCorrectAnswerInput, setShowCorrectAnswerInput] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [key, setKey] = useState(0);
  // Add timer-related state
  const [currentTimerZone, setCurrentTimerZone] = useState<'green' | 'yellow'>('green');
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [timerKey, setTimerKey] = useState(0);
  const [factAttempts, setFactAttempts] = useState<Record<string, {
    attempts: number;
    correct: number;
    greenZoneCorrect: number; // Track correct answers in green zone
    timeSpent: number;
  }>>({});
  // Simple state to track mastery progress for each fact
  const [factProgress, setFactProgress] = useState<Record<string, number>>({});
  const [masteredFacts, setMasteredFacts] = useState<Set<string>>(new Set());
  // Add a ref to track recently shown facts
  const recentlyShownFactsRef = useRef<string[]>([]);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(false);
  const [showLevelTransition, setShowLevelTransition] = useState(false);
  const [levelTransitionKey, setLevelTransitionKey] = useState(0);
  
  // Streak animation
  const prevStreakRef = useRef(0);
  const streakControls = useAnimationControls();

  // Card feedback animation
  const cardControls = useAnimationControls();

  useEffect(() => {
    if (currentStreak === undefined) return;
    if (currentStreak > prevStreakRef.current) {
      streakControls.start({
        scale: [1, 1.3, 1],
        transition: { duration: 0.3, ease: 'easeOut' },
      });
    } else if (currentStreak === 0 && prevStreakRef.current > 0) {
      streakControls.start({
        x: [0, -4, 4, -4, 4, 0],
        transition: { duration: 0.4 },
      });
    }
    prevStreakRef.current = currentStreak;
  }, [currentStreak, streakControls]);

  const { user } = useAuth();
  const practiceMasteryThreshold = getAccommodationMasteryThreshold(user?.iepAccommodationsEnabled);
  const isUntimed = isAccommodationTimerUntimed(user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const effectiveTimerDuration = getAccommodationTimerDuration(
    timerDuration,
    user?.iepAccommodationsEnabled,
    user?.iepTimerMode
  );
  const answerRevealDelayMs = getAccommodationTimerDuration(
    12_000,
    user?.iepAccommodationsEnabled,
    user?.iepTimerMode
  );

  // Calculate total repetitions needed for all facts
  const totalRepetitionsNeeded = useMemo(() => {
    return facts.reduce((total, fact) => {
      const isPracticeFact = practiceFacts.includes(fact.PK);
      const threshold = isPracticeFact ? practiceMasteryThreshold : OTHER_MASTERY_THRESHOLD;
      return total + threshold;
    }, 0);
  }, [facts, practiceFacts, practiceMasteryThreshold]);

  // Calculate current progress toward mastery
  const currentMasteryProgress = useMemo(() => {
    const totalProgress = Object.entries(factProgress).reduce((total, [factId, progress]) => {
      const isPracticeFact = practiceFacts.includes(factId);
      const threshold = isPracticeFact ? practiceMasteryThreshold : OTHER_MASTERY_THRESHOLD;
      // Only cap the upper limit per question
      const cappedProgress = Math.min(progress, threshold);
      return total + cappedProgress;
    }, 0);
    
    // Return the total progress without limiting to 0
    return totalProgress;
  }, [factProgress, practiceFacts, practiceMasteryThreshold]);

  // Function to determine if a fact is mastered based on its group
  const isMastered = (factId: string, correctAnswers: number) => {
    const isPracticeFact = practiceFacts.includes(factId);
    
    if (isPracticeFact) {
      return correctAnswers >= practiceMasteryThreshold;
    } else {
      return correctAnswers >= OTHER_MASTERY_THRESHOLD;
    }
  };

  // Timer event handlers
  const handleTimerZoneChange = (zone: 'green' | 'yellow') => {
    setCurrentTimerZone(zone);
    if (zone === 'yellow') {
      onTimerWarning?.();
    }
  };

  const handleTimerTimeout = () => {
    // When timer runs out completely, treat it as an incorrect answer
    // and show the correct answer input
    setIsTimerActive(false);
    
    // Update fact progress as incorrect
    const currentQuestion = facts[currentIndex];
    if (currentQuestion) {
      const factId = currentQuestion.PK;
      const currentProgress = factProgress[factId] || 0;
      const newCorrectAnswers = currentProgress; // No penalty, just don't add progress
      
      setFactProgress(prev => ({
        ...prev,
        [factId]: newCorrectAnswers
      }));
      
      // Check if mastery status changed
      const wasMastered = masteredFacts.has(factId);
      const nowMastered = isMastered(factId, newCorrectAnswers);
      
      // Update mastered facts if status changed
      if (wasMastered && !nowMastered) {
        setMasteredFacts(prev => {
          const newSet = new Set(Array.from(prev));
          newSet.delete(factId);
          return newSet;
        });
      }
      
      // Update fact attempts
      setFactAttempts(prev => {
        const newAttempts = {
          ...prev,
          [factId]: {
            attempts: (prev[factId]?.attempts || 0) + 1,
            correct: prev[factId]?.correct || 0,
            greenZoneCorrect: prev[factId]?.greenZoneCorrect || 0, // No increment as answer is incorrect
            timeSpent: (prev[factId]?.timeSpent || 0) + 12000 // Assume max time
          }
        };
        
        return newAttempts;
      });
      
      // Notify parent of timeout (incorrect)
      onAnswerResult?.(false);

      // Show the correct answer input instead of moving to next question
      setShowCorrectAnswerInput(true);
    }
  };

  // Reset timer for new question
  const resetTimer = useCallback(() => {
    setTimerKey(prev => prev + 1);
    setCurrentTimerZone('green');
    // Don't set isTimerActive here, as it's controlled by the parent logic
  }, []);

  // Refs
  const testId = useRef(crypto.randomUUID());
  const questionsAnsweredRef = useRef(questionsAnswered);
  const correctAnswersRef = useRef(correctAnswers);
  const isSubmitting = useRef<boolean>(false);
  const progressUpdatePromises = useRef<Promise<any>[]>([]);
  const isMounted = useRef(true);
  const questionStartTime = useRef<number>(Date.now());
  const showAnswerTimer = useRef<NodeJS.Timeout | null>(null);

  // Convert facts to questions format
  const questions = useMemo(() => {
    return facts.map(fact => ({
      id: fact.PK,
      num1: fact.operand1,
      num2: fact.operand2,
      operator: fact.operation === 'addition' ? '+' : 
                fact.operation === 'subtraction' ? '−' : 
                fact.operation === 'multiplication' ? '×' : '÷',
      answer: fact.result
    }));
  }, [facts]);

  // Initialize factAttempts and factProgress
  useEffect(() => {
    const initialFactAttempts: Record<string, {
      attempts: number;
      correct: number;
      greenZoneCorrect: number;
      timeSpent: number;
    }> = {};
    
    const initialProgress: Record<string, number> = {};
    
    questions.forEach(question => {
      const factId = question.id;
      initialFactAttempts[factId] = {
        attempts: 0,
        correct: 0,
        greenZoneCorrect: 0,
        timeSpent: 0
      };
      initialProgress[factId] = 0;
    });
    
    setFactAttempts(initialFactAttempts);
    setFactProgress(initialProgress);
  }, [questions]);

  // Update refs when state changes
  useEffect(() => {
    questionsAnsweredRef.current = questionsAnswered;
  }, [questionsAnswered]);

  useEffect(() => {
    correctAnswersRef.current = correctAnswers;
  }, [correctAnswers]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (progressUpdatePromises.current.length > 0) {
        Promise.all(progressUpdatePromises.current).catch(error => {
          console.error('[Cleanup Error]:', error);
        });
      }
      if (showAnswerTimer.current) {
        clearTimeout(showAnswerTimer.current);
      }
    };
  }, []);

  // Update questionStartTime when moving to a new question
  useEffect(() => {
    questionStartTime.current = Date.now();
    
    // Reset the timer when moving to a new question
    resetTimer();
    
    // Reset transition state
    setIsTransitioning(false);
    
    // Set a timer to show the answer after 12 seconds
    if (!showCorrectAnswerInput && !isUntimed) {
      showAnswerTimer.current = setTimeout(() => {
        if (isMounted.current) {
          setShowCorrectAnswerInput(true);
        }
      }, answerRevealDelayMs);
      
      return () => {
        if (showAnswerTimer.current) {
          clearTimeout(showAnswerTimer.current);
          showAnswerTimer.current = null;
        }
      };
    }
  }, [answerRevealDelayMs, currentIndex, isUntimed, showCorrectAnswerInput, resetTimer]);


  const handlePracticeEnd = async () => {
    try {
      if (isProcessingEnd) return;
      setIsProcessingEnd(true);

      // Wait for all progress updates to complete
      if (progressUpdatePromises.current.length > 0) {
        await Promise.all(progressUpdatePromises.current);
      }

      const masteredPracticeCount = Array.from(masteredFacts).filter(pk => practiceFacts.includes(pk)).length;
      setIsProcessingEnd(false);
      onComplete(masteredPracticeCount);
    } catch (error) {
      console.error('[QuestionScreen] Error handling practice end:', error);

      // Log the error using our utility
      logError(error, {
        component: 'FluencyPractice-QuestionScreen',
        trackId,
        userId: user?.userId,
        pendingUpdates: progressUpdatePromises.current.length
      });

      // Allow user to continue despite the error
      const masteredPracticeCount = Array.from(masteredFacts).filter(pk => practiceFacts.includes(pk)).length;
      setIsProcessingEnd(false);
      onComplete(masteredPracticeCount);
    }
  };

  // Update the handleAnswer function to consider timer state
  const handleAnswer = async (answer: number) => {
    if (!facts[currentIndex] || isTransitioning) {
      return;
    }

    // Pause the timer when an answer is submitted
    setIsTimerActive(false);

    // Clear the show answer timer
    if (showAnswerTimer.current) {
      clearTimeout(showAnswerTimer.current);
      showAnswerTimer.current = null;
    }

    const currentQuestion = facts[currentIndex];
    const isCorrect = answer === currentQuestion.result;
    
    // Determine if the answer is truly correct based on timer zone
    // In yellow zone, we count it as incorrect for mastery purposes
    const isTrulyCorrect = isCorrect && (isUntimed || currentTimerZone === 'green');
    
    // Set lastAnswerCorrect based on the actual correctness of the answer
    // This affects the visual feedback
    setLastAnswerCorrect(isCorrect);

    // Notify parent of answer result for sound effects etc.
    onAnswerResult?.(isTrulyCorrect);

    const totalTimeSpentMs = Date.now() - questionStartTime.current;
    const factId = currentQuestion.PK;

    // Update mastery progress - consider timer zone for fluency practice
    const currentProgress = factProgress[factId] || 0;
    
    // Adjust scoring based on timer zone
    let newCorrectAnswers: number;
    if (isTrulyCorrect) {
      // Only give credit for green zone correct answers
      newCorrectAnswers = currentProgress + 1;
    } else {
      // Yellow zone correct answers or incorrect answers don't add to progress
      newCorrectAnswers = currentProgress;
    }
    
    setFactProgress(prev => ({
      ...prev,
      [factId]: newCorrectAnswers
    }));
    
    // Create a local variable to track mastery status changes for this fact
    let shouldAddToMastered = false;
    let shouldRemoveFromMastered = false;
    
    // Check if this fact is now mastered or unmastered
    const wasMastered = masteredFacts.has(factId);
    const nowMastered = isMastered(factId, newCorrectAnswers);
    
    if (!wasMastered && nowMastered) {
      shouldAddToMastered = true;
    } else if (wasMastered && !nowMastered) {
      shouldRemoveFromMastered = true;
    }
    
    // Update fact attempts
    setFactAttempts(prev => {
      const newAttempts = {
        ...prev,
        [factId]: {
          attempts: (prev[factId]?.attempts || 0) + 1,
          correct: (prev[factId]?.correct || 0) + (isCorrect ? 1 : 0),
          greenZoneCorrect: (prev[factId]?.greenZoneCorrect || 0) + (isTrulyCorrect ? 1 : 0),
          timeSpent: (prev[factId]?.timeSpent || 0) + totalTimeSpentMs
        }
      };
      
      return newAttempts;
    });
    
    // Show feedback briefly
    setShowFeedback(true);

    // Card-level visual feedback
    if (isCorrect) {
      cardControls.start({
        boxShadow: [
          '0 0 0 0 rgba(34,197,94,0)',
          '0 0 20px 4px rgba(34,197,94,0.4)',
          '0 0 0 0 rgba(34,197,94,0)',
        ],
        transition: { duration: 0.4 },
      });
    } else {
      cardControls.start({
        x: [0, -6, 6, -6, 6, 0],
        transition: { duration: 0.4 },
      });
    }

    // If the answer is incorrect and we're in green zone, show the correct answer input
    if (!isCorrect) {
      // For incorrect answers regardless of timer zone, switch to CorrectAnswerInput
      setTimeout(() => {
        setShowFeedback(false);
        setShowCorrectAnswerInput(true);
      }, 400);
    } else {
      // For correct answers, move to the next question after a brief feedback display
      setTimeout(() => {
        setShowFeedback(false);
        moveToNextQuestion(
          shouldAddToMastered ? factId : null,
          shouldRemoveFromMastered ? factId : null
        );
      }, 400);
    }

    // Update progress
    if (user?.userId) {
      try {
        // Get current practice context from session storage
        const practiceContextData = sessionStorage.getItem('practiceContext');
        let practiceContext = '';
        if (practiceContextData) {
          try {
            const parsed = JSON.parse(practiceContextData);
            practiceContext = parsed.current || '';
          } catch (error) {
            console.error('Error parsing practiceContext for progress update:', error);
          }
        }

        // Send progress to API via queue
        progressQueue.enqueueProgressUpdate(
          user.userId,
          {
            ...(immediateProgress && { immediate: true }),
            facts: {
              [factId.split('FACT#')[1]]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: totalTimeSpentMs,
                practiceContext: practiceContext
              }
            }
          }
        );
      } catch (error) {
        // Log the error using our utility
        logError(error, {
          component: 'FluencyPractice-ProgressUpdate',
          trackId,
          userId: user.userId,
          factId,
          isCorrect,
          timeSpent: totalTimeSpentMs,
          networkConnected: navigator.onLine,
          connectionType: (navigator as any).connection?.effectiveType || 'unknown'
        });
      }
    }
  };

  const handleCorrectInput = () => {
    setShowCorrectAnswerInput(false);
    setQuestionsAnswered(prev => prev + 1);
    
    // Move to the next question and reset the timer
    moveToNextQuestion();
  };

  const moveToNextQuestion = (addToMastered: string | null = null, removeFromMastered: string | null = null) => {
    // Don't allow transitions while one is in progress
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    
    // Update mastered facts set if needed
    if (addToMastered) {
      setMasteredFacts(prev => new Set([...Array.from(prev), addToMastered]));
    }
    if (removeFromMastered) {
      setMasteredFacts(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(removeFromMastered);
        return newSet;
      });
    }
    
    // Reset feedback state
    setShowFeedback(false);
    
    // Create a temporary masteredFacts set to use for this function
    const tempMasteredFacts = new Set(masteredFacts);
    if (addToMastered) tempMasteredFacts.add(addToMastered);
    if (removeFromMastered) tempMasteredFacts.delete(removeFromMastered);
    
    // If all facts are mastered, end the practice
    const allMastered = facts.every(fact => tempMasteredFacts.has(fact.PK));
    if (allMastered) {
        handlePracticeEnd();
        return;
    }
    
    setShowCorrectAnswerInput(false);
    
    // Reset timer for the next question
    resetTimer();
    // Ensure timer is active for the next question
    setIsTimerActive(true);
    
    // Increment current index to move to next question
    setCurrentIndex(prevIndex => {
      // Get the current fact ID to update recently shown facts
      const currentFactId = facts[prevIndex]?.PK;
      
      // Update recently shown facts using the ref for immediate access
      if (currentFactId) {
        const updated = [...recentlyShownFactsRef.current];
        // Remove current fact if it's already in the list
        const existingIndex = updated.indexOf(currentFactId);
        if (existingIndex !== -1) {
          updated.splice(existingIndex, 1);
        }
        // Add current fact to the front of the array
        updated.unshift(currentFactId);
        // Keep only the 3 most recent facts
        recentlyShownFactsRef.current = updated.slice(0, 3);
      }
      
      // Prioritize non-mastered facts that haven't reached the attempt limit
      const nonMasteredFactIndices = facts
        .map((fact, index) => ({ fact, index }))
        .filter(item => {
          const factId = item.fact.PK;
          const attempts = factAttempts[factId]?.attempts || 0;
          return !tempMasteredFacts.has(factId) && attempts < MAX_ATTEMPTS_PER_FACT;
        })
        .map(item => item.index);
      
      if (nonMasteredFactIndices.length > 0) {
        // Filter out recently shown facts from the selection pool
        let availableIndices = nonMasteredFactIndices.filter(
          index => !recentlyShownFactsRef.current.includes(facts[index].PK)
        );
        
        // If filtering out recent facts leaves us with no options, fall back to all non-mastered facts
        if (availableIndices.length === 0) {
          availableIndices = nonMasteredFactIndices;
        }
        
        // Choose a random fact from the available indices
        const randomIndex = availableIndices[
          Math.floor(Math.random() * availableIndices.length)
        ];
        
        // Ensure we're not selecting the current fact again if there are other options
        if (randomIndex === prevIndex && availableIndices.length > 1) {
          // Remove the current index from available indices
          availableIndices = availableIndices.filter(index => index !== prevIndex);
          // Select from the remaining indices
          const newRandomIndex = availableIndices[
            Math.floor(Math.random() * availableIndices.length)
          ];
          return newRandomIndex;
        } else {
          return randomIndex;
        }
      } else {
        // If all facts are mastered or have reached attempt limit, end the practice
        handlePracticeEnd();
        return prevIndex;
      }
    });

    // Reset isTransitioning state after a short delay
    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  // Reset component state and show level transition overlay when the effective timer changes
  useEffect(() => {
    setCurrentIndex(0);
    setQuestionsAnswered(0);
    setCorrectAnswers(0);
    setShowFeedback(false);
    setIsProcessingEnd(false);
    setShowCorrectAnswerInput(false);
    setIsTransitioning(false);
    setKey(prev => prev + 1);
    setCurrentTimerZone('green');
    setIsTimerActive(false);
    setTimerKey(prev => prev + 1);
    setFactAttempts({});
    setFactProgress({});
    setMasteredFacts(new Set());
    recentlyShownFactsRef.current = [];
    resetTimer();

    // Show level transition overlay
    setShowLevelTransition(true);
    setLevelTransitionKey(prev => prev + 1);

    // Hide overlay after 3 seconds and start the timer
    const timeout = setTimeout(() => {
      setShowLevelTransition(false);
      setIsTimerActive(true);
      questionStartTime.current = Date.now();
    }, 3000);

    return () => clearTimeout(timeout);
  }, [effectiveTimerDuration, isUntimed, resetTimer]);

  // Calculate total attempts for Start Over button
  const totalAttempts = useMemo(() => {
    return Object.values(factAttempts).reduce((sum, fact) => sum + (fact.attempts || 0), 0);
  }, [factAttempts]);
  const ATTEMPT_THRESHOLD = 40;

  // Conditional returns
  if (!facts || facts.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div>No questions available</div>
      </div>
    );
  }

  const currentQuestion = facts[currentIndex];
  if (!currentQuestion) {
    return null;
  }

  if (isProcessingEnd) {
    return (
      <AnimatePresence>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center"
        >
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-lg text-gray-600">Processing final results...</p>
        </motion.div>
      </AnimatePresence>
    );
  }

  const renderInputMethod = () => {
    if (showCorrectAnswerInput) {
      return (
        <CorrectAnswerInput
          correctAnswer={currentQuestion.result}
          onCorrectInput={handleCorrectInput}
          question={{
            num1: currentQuestion.operand1,
            num2: currentQuestion.operand2,
            operator: currentQuestion.operation === 'addition' ? '+' : 
                     currentQuestion.operation === 'subtraction' ? '-' : 
                     currentQuestion.operation === 'multiplication' ? '×' : '÷'
          }}
        />
      );
    }

    // Create a compatible question object from the Fact
    const questionForInput = {
      num1: currentQuestion.operand1,
      num2: currentQuestion.operand2,
      operator: currentQuestion.operation === 'addition' ? '+' : 
                currentQuestion.operation === 'subtraction' ? '-' : 
                currentQuestion.operation === 'multiplication' ? '×' : '÷',
      answer: currentQuestion.result
    };
    
    // Use the new FluencyTextInput component
    return (
      <FluencyTextInput
        question={questionForInput}
        onAnswer={handleAnswer}
        showFeedback={showFeedback}
        isCorrect={lastAnswerCorrect}
        timerKey={timerKey}
        hideKeypad={hideKeypad}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6 flex flex-col justify-center relative">
      {/* Streak Counter — top-right floating badge (only when parent passes streak props) */}
      {currentStreak !== undefined && (
        <motion.div
          animate={streakControls}
          className="absolute top-4 right-4 flex flex-col items-center z-10"
        >
          <div
            className={`flex items-center gap-1.5 px-4 py-2 rounded-2xl shadow-lg transition-colors duration-300 ${
              currentStreak > 0
                ? 'bg-white border border-indigo-100'
                : 'bg-white/70 border border-gray-200'
            }`}
          >
            <Flame
              className={`w-5 h-5 transition-colors duration-300 ${
                currentStreak > 0 ? 'text-orange-500' : 'text-gray-300'
              }`}
            />
            <span
              className={`text-2xl font-black transition-colors duration-300 ${
                currentStreak > 0 ? 'text-indigo-600' : 'text-gray-300'
              }`}
            >
              {currentStreak}
            </span>
          </div>
          {(sessionBestStreak ?? 0) > 0 && (
            <span className="text-xs text-gray-400 mt-1 font-medium">
              Best: {sessionBestStreak}
            </span>
          )}
        </motion.div>
      )}

      <div className="max-w-4xl w-full mx-auto space-y-6">
        {/* Progress Bar - directly in the container, not in a card */}
        <ProgressBar current={currentMasteryProgress} total={totalRepetitionsNeeded} />
        
        {/* Main content card */}
        <motion.div animate={cardControls} className="bg-white rounded-xl shadow-lg p-6 md:p-8 relative">
          {/* Timer component */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-gray-500">
                {isUntimed ? 'Timer: Untimed' : `Timer Speed: ${effectiveTimerDuration}s`}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                isUntimed || effectiveTimerDuration >= 6 ? 'bg-green-100 text-green-800' :
                effectiveTimerDuration >= 3 ? 'bg-blue-100 text-blue-800' :
                effectiveTimerDuration >= 2 ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {isUntimed ? 'No limit' :
                 effectiveTimerDuration >= 6 ? 'Beginner' :
                 effectiveTimerDuration >= 3 ? 'Intermediate' :
                 effectiveTimerDuration >= 2 ? 'Advanced' :
                 'Expert'}
              </span>
            </div>
            <Timer
              key={timerKey}
              duration={effectiveTimerDuration}
              onTimeout={handleTimerTimeout}
              onZoneChange={handleTimerZoneChange}
              isActive={isTimerActive && !showFeedback && !showCorrectAnswerInput}
              untimed={isUntimed}
            />
          </div>
          
          {/* Question display */}
          {currentQuestion && (
            <div className="rounded-lg shadow-md p-6 border-2 bg-white border-gray-200">
              <div key={key}>
                {renderInputMethod()}
              </div>
            </div>
          )}
        </motion.div>
        {/* Start Over button after threshold */}
        {totalAttempts > ATTEMPT_THRESHOLD && (
          <div className="flex justify-center mt-6">
            <button
              className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold shadow-md transition-all duration-200"
              onClick={() => { window.location.href = '/fluency-practice'; }}
            >
              Start Over
            </button>
          </div>
        )}
      </div>
      
      {/* Processing overlay */}
      {isProcessingEnd && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-lg text-gray-600">Processing final results...</p>
        </div>
      )}

      {/* Level transition overlay */}
      <AnimatePresence>
        {showLevelTransition && (
          <motion.div
            key={levelTransitionKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md"
            >
              <h2 className="text-2xl font-bold mb-2">
                {isUntimed ? 'Timer: Untimed' : `New Timer Speed: ${effectiveTimerDuration}s`}
              </h2>
              <p className="mb-6 text-gray-500">Get ready for the next level!</p>
              <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-indigo-500 rounded-full"
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: 3, ease: 'linear' }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuestionScreen;
